/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { MatDialog } from "@angular/material/dialog";
import { of } from "rxjs";
import { MyInjector } from "@src/app/app.module";
import { uuid } from "@src/app/model/abstract-object";
import { ContactTypes, EntityTypes, Person } from "@src/app/model/person";
import { ActionColor, IFormAction } from "@src/app/shared/form/form.component";
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { FieldSet, LAYOUT_OPTIONS } from "@src/app/shared/form/field-set/field-set.component";
import { FieldMaker } from "@src/app/shared/field/FieldMaker";
import { FormTextComponent } from "@src/app/shared/form/form-text/form-text.component";
import { FormTextAreaComponent } from "@src/app/shared/form/form-text-area/form-text-area.component";
import { EditDialogComponent } from "@src/app/shared/dialogs/edit-dialog/edit-dialog.component";
import { map } from "rxjs/operators";
import { PersonService } from "@src/app/pages/person-page/person.service";
import { FormPicklistComponent } from "@src/app/shared/form/form-picklist/form-picklist.component";
import { required } from "@src/app/shared/validators";

export class AssociateContactAction implements IFormAction {
    name = $localize`Associate Contact`;
    color: ActionColor = 'primary';
    show = false;
    icon = 'add';
    approvalNeeded = false;
    disabled = false;
    approvalText = "";
    tipText = $localize `Create a new contact associated to this contact`;
    teamId: uuid;
    person: Person;

    personSvc = MyInjector.instance.get(PersonService);
    dialog = MyInjector.instance.get(MatDialog);

    getConfig() : FormConfig {
        return new FormConfig({
            fieldSet: new FieldSet({
                fields: [
                    FieldMaker.id(),
                    FieldMaker.rev(),
                    FieldMaker.idHolder('primaryContactId'),
                    Person.getTitleField().override({formColumn: 1}),
                    FormTextComponent.make('First Name', 'firstName', {formColumn: 1}),
                    FormTextComponent.make('Last Name', 'lastName', {formColumn: 1, validators: [required]}),
                    FormTextComponent.make('Telephone', 'phone',{formColumn: 1}),
                    FormTextComponent.make('Email', 'email', {formColumn: 1}),                    
                    FormTextComponent.make('Primary Contact Name', 'primaryContactName', {readonly: true, disable: true, formColumn: 2}),
                    FormPicklistComponent.make('Entity Type', 'entityType', null, {items: EntityTypes.items}, {formColumn: 2}),
                    FormPicklistComponent.make('Contact Type', 'contactType', null, {items: ContactTypes.omcSelectables(),}, {formColumn: 2}),
                    FormTextAreaComponent.make('Notes', 'notes', {formColumn: 2}),
                ],
                formLayout: LAYOUT_OPTIONS.doubleCol,
            }),
            title: $localize`Associated Contact`,
            mode: 'new',
            service: this.personSvc,
            objectFactory: () => { 
                const p = new Person();
                p.primaryContactId = this.person.id;
                p.primaryContactName = this.person.fullName;
                return of(p);
            },
        });        
    }

    action() {
        const dialogRef = this.dialog.open(EditDialogComponent, {data: { 
            config: this.getConfig(),
            forceTeamId: this.person.teamId,
            mode: 'new',
            hideTabs: true,
            width: 800,
        }});
        return dialogRef.afterClosed().pipe(map(p => {
            if (p) {
                this.person.associatedContacts.push(p);
            }
            return this.person;
        }));        
    }
    
    setup(person: Person) {
        this.person = person;
        this.show = true;

    }
}
